import React, {useState, useRef, useEffect} from 'react';
import {MdKeyboardArrowDown} from 'react-icons/md';

import {text} from '../text';
import {theme} from '../constants';

const ArrowIcon = MdKeyboardArrowDown as any;

interface Option {
  id: string | number;
  name: string;
  [key: string]: any;
}

interface Props {
  options: Option[];
  value: string | number;
  onChange: (value: string) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  leftIcon?: React.ReactNode;
  disabled?: boolean;
  containerStyle?: React.CSSProperties;
}

export const SearchableDropdown: React.FC<Props> = ({
  options,
  value,
  onChange,
  placeholder = 'Select...',
  searchPlaceholder = 'Search...',
  leftIcon,
  disabled = false,
  containerStyle,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filteredOptions = options.filter((option) =>
    option.name.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  const selectedOption = options.find(
    (o) => o.id.toString() === value?.toString(),
  );

  return (
    <div
      ref={dropdownRef}
      style={{marginBottom: 10, position: 'relative', ...containerStyle}}
    >
      {leftIcon && (
        <div
          style={{
            position: 'absolute',
            paddingLeft: 10,
            paddingTop: 15,
            zIndex: 1,
            pointerEvents: 'none',
          }}
        >
          {leftIcon}
        </div>
      )}

      <div
        onClick={() => !disabled && setIsOpen(!isOpen)}
        style={{
          width: '100%',
          height: 50,
          paddingLeft: leftIcon ? 50 : 20,
          paddingRight: 20,
          borderRadius: 10,
          border: '1px solid #FFEFE6',
          backgroundColor: disabled ? '#f9f9f9' : theme.colors.white,
          display: 'flex',
          alignItems: 'center',
          cursor: disabled ? 'not-allowed' : 'pointer',
          userSelect: 'none',
        }}
      >
        <text.T16
          style={{
            color: selectedOption ? theme.colors.mainDark : '#9CA3AF',
            textTransform: 'capitalize',
            ...theme.fonts.SourceSansPro_400Regular,
          }}
        >
          {selectedOption ? selectedOption.name : placeholder}
        </text.T16>
      </div>

      {isOpen && (
        <div
          style={{
            position: 'absolute',
            top: '105%',
            left: 0,
            right: 0,
            backgroundColor: 'white',
            borderRadius: 10,
            boxShadow: '0px 4px 12px rgba(0,0,0,0.1)',
            zIndex: 100,
            border: '1px solid #f0f0f0',
            overflow: 'hidden',
          }}
        >
          <div style={{padding: 10, borderBottom: '1px solid #f0f0f0'}}>
            <input
              type='text'
              placeholder={searchPlaceholder}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              autoFocus
              style={{
                width: '100%',
                padding: '8px 12px',
                borderRadius: 8,
                boxSizing: 'border-box',
                border: '1px solid #ddd',
                outline: 'none',
                fontSize: 14,
              }}
              onClick={(e) => e.stopPropagation()}
            />
          </div>
          <div style={{maxHeight: 200, overflowY: 'auto'}}>
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option) => (
                <div
                  key={option.id}
                  onClick={() => {
                    onChange(option.id.toString());
                    setIsOpen(false);
                    setSearchTerm('');
                  }}
                  style={{
                    padding: '12px 15px',
                    cursor: 'pointer',
                    borderBottom: '1px solid #f9f9f9',
                    backgroundColor:
                      value?.toString() === option.id.toString()
                        ? '#F3F4F6'
                        : 'white',
                    textTransform: 'capitalize',
                    fontSize: 14,
                    color: theme.colors.mainDark,
                  }}
                >
                  {option.name}
                </div>
              ))
            ) : (
              <div
                style={{
                  padding: 15,
                  textAlign: 'center',
                  color: '#9CA3AF',
                  fontSize: 14,
                }}
              >
                No options found
              </div>
            )}
          </div>
        </div>
      )}

      <div
        style={{
          position: 'absolute',
          right: 20,
          top: 18,
          zIndex: 1,
          pointerEvents: 'none',
        }}
      >
        <ArrowIcon size={20} color={theme.colors.mainDark} />
      </div>
    </div>
  );
};